import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

const PLACE_NAMES = ['個', '十', '百', '千'] as const;
type PlaceName = typeof PLACE_NAMES[number];

/**
 * N-3-1：萬以內的數——位值認識（個十百千）。
 * 三上第一章：指定位值的數字是幾？
 *
 * difficulty 1：三位數，問個位或十位
 * difficulty 2：四位數，問百位或千位
 * difficulty 3：四位數（含 0 位），問任意位
 */
export function generatePlaceValue(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let num: number;
  let placeIdx: number;

  if (difficulty === 1) {
    num = randInt(rand, 100, 999);
    placeIdx = randInt(rand, 0, 1); // 個 or 十
  } else if (difficulty === 2) {
    num = randInt(rand, 1000, 9999);
    placeIdx = randInt(rand, 2, 3); // 百 or 千
  } else {
    // d3: four-digit, random place, may include zeros
    num = randInt(rand, 1000, 9999);
    placeIdx = randInt(rand, 0, 3);
  }

  const placeName: PlaceName = PLACE_NAMES[placeIdx];
  const correctDigit = Math.floor(num / Math.pow(10, placeIdx)) % 10;

  // Distractors: other digits in the number + one random nearby
  const distractors = new Set<number>();
  for (let i = 0; i < 4; i++) {
    if (i !== placeIdx) {
      const d = Math.floor(num / Math.pow(10, i)) % 10;
      if (d !== correctDigit) distractors.add(d);
    }
  }
  // Fill up to 3 distractors
  while (distractors.size < 3) {
    const candidate = randInt(rand, 0, 9);
    if (candidate !== correctDigit) distractors.add(candidate);
  }

  const optionList = [correctDigit, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed * 13 + x)() - mulberry32(seed * 13 + y)());
  const correctIdx = shuffled.indexOf(correctDigit);

  return {
    kind: 'mcq',
    prompt: `${num} 的${placeName}位是幾？`,
    options: shuffled,
    correctAnswer: correctIdx,
    explanation: `${num} 中，${placeName}位的數字是 ${correctDigit}。（位值：千${Math.floor(num / 1000)}、百${Math.floor(num / 100) % 10}、十${Math.floor(num / 10) % 10}、個${num % 10}）`,
    kp: 'math.place.value.4digits',
  };
}
